import { config } from '../config/environment.js'

const API_BASE_URL = config.API_BASE_URL

// Función para obtener el token del localStorage
const getToken = () => {
  return localStorage.getItem('authToken')
}

// Función para guardar el token en localStorage
const setToken = (token) => {
  localStorage.setItem('authToken', token)
}

// Función para eliminar el token del localStorage
const removeToken = () => {
  localStorage.removeItem('authToken')
}

// Función para hacer peticiones HTTP
const apiRequest = async (endpoint, options = {}) => {
  // Si API_BASE_URL ya incluye '/api', no lo duplicamos
  const baseUrl = API_BASE_URL.endsWith('/api') ? API_BASE_URL : `${API_BASE_URL}/api`
  const url = `${baseUrl}${endpoint}`
  const token = getToken()
  
  const requestConfig = {
    headers: {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options.headers
    },
    ...options
  }

  try {
    const response = await fetch(url, requestConfig)
    const data = await response.json()

    if (!response.ok) {
      throw new Error(data.error || 'Error en la petición')
    }

    return data
  } catch (error) {
    console.error('Error en API:', error)
    throw error
  }
}

// Servicios de autenticación
export const authService = {
  // Registrar usuario
  register: async (userData) => {
    const response = await apiRequest('/users/register', {
      method: 'POST',
      body: JSON.stringify(userData)
    })
    
    if (response.token) {
      setToken(response.token)
    }
    
    return response
  },

  // Iniciar sesión
  login: async (credentials) => {
    const response = await apiRequest('/users/login', {
      method: 'POST',
      body: JSON.stringify(credentials)
    })
    
    if (response.token) {
      setToken(response.token)
    }
    
    return response
  },

  // Cerrar sesión
  logout: () => {
    removeToken()
  },

  // Verificar si el usuario está autenticado
  isAuthenticated: () => {
    return !!getToken()
  },

  // Obtener token
  getToken
}

// Servicios de usuario
export const userService = {
  // Obtener perfil del usuario
  getProfile: async () => {
    return await apiRequest('/users/profile')
  },

  // Actualizar perfil del usuario
  updateProfile: async (userData) => {
    return await apiRequest('/users/profile', {
      method: 'PUT',
      body: JSON.stringify(userData)
    })
  },

  // Obtener todos los usuarios (solo admin)
  getAllUsers: async () => {
    return await apiRequest('/users')
  },

  // Eliminar usuario (solo admin)
  deleteUser: async (userId) => {
    return await apiRequest(`/users/${userId}`, {
      method: 'DELETE'
    })
  },

  // Actualizar usuario (solo admin)
  updateUser: async (userId, userData) => {
    return await apiRequest(`/users/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(userData)
    })
  }
}

// Servicios de estadísticas
export const statsService = {
  // Obtener estadísticas del dashboard
  getDashboardStats: async () => {
    return await apiRequest('/stats/dashboard')
  },

  // Obtener actividad reciente
  getRecentActivity: async () => {
    return await apiRequest('/stats/recent-activity')
  },

  // Agregar nueva actividad
  addActivity: async (activityData) => {
    return await apiRequest('/stats/activity', {
      method: 'POST',
      body: JSON.stringify(activityData)
    })
  }
}

// Servicios de membresías
export const membresiaService = {
  // Obtener todas las membresías
  getAllMembresias: async () => {
    return await apiRequest('/sorteos')
  },

  // Obtener una membresía específica
  getMembresia: async (id) => {
    return await apiRequest(`/sorteos/${id}`)
  },

  // Crear nueva membresía (solo admin)
  createMembresia: async (membresiaData) => {
    return await apiRequest('/sorteos', {
      method: 'POST',
      body: JSON.stringify(membresiaData)
    })
  },

  // Actualizar membresía (solo admin)
  updateMembresia: async (id, membresiaData) => {
    return await apiRequest(`/sorteos/${id}`, {
      method: 'PUT',
      body: JSON.stringify(membresiaData)
    })
  },

  // Eliminar membresía (solo admin)
  deleteMembresia: async (id) => {
    return await apiRequest(`/sorteos/${id}`, {
      method: 'DELETE'
    })
  },

  // Obtener la última membresía activa
  getLatestMembresia: async () => {
    const membresias = await apiRequest('/sorteos')
    const activeMembresias = membresias.filter(membresia => membresia.status === 'active')
    return activeMembresias.length > 0 ? activeMembresias[0] : null
  },

  // Subir imagen para membresía
  uploadImage: async (formData) => {
    const token = getToken()
    const response = await fetch(`${API_BASE_URL}/upload/image`, {
      method: 'POST',
      headers: {
        ...(token && { Authorization: `Bearer ${token}` })
      },
      body: formData
    })

    const data = await response.json()

    if (!response.ok) {
      throw new Error(data.error || 'Error al subir imagen')
    }

    return data
  }
}

// Servicios de transacciones
export const transactionService = {
  // Crear transacción de compra de boletos (público, sin autenticación)
  createTransaction: async (transactionData) => {
    return await apiRequest('/transactions/create-public', {
      method: 'POST',
      body: JSON.stringify(transactionData)
    })
  },

  // Crear transacción autenticada (para usuarios logueados)
  createAuthenticatedTransaction: async (transactionData) => {
    return await apiRequest('/transactions/create', {
      method: 'POST',
      body: JSON.stringify(transactionData)
    })
  },

  // Obtener boletos del usuario
  getMyTickets: async () => {
    return await apiRequest('/transactions/my-tickets')
  },

  // Completar pago (solo admin)
  completePayment: async (transactionId) => {
    return await apiRequest(`/transactions/complete/${transactionId}`, {
      method: 'PUT'
    })
  },

  // Actualizar estado de transacción
  updateTransactionStatus: async (reference, status) => {
    return await apiRequest(`/transactions/update-status/${reference}`, {
      method: 'PUT',
      body: JSON.stringify({ status })
    })
  },

  // Obtener estado de transacción (solo API local)
  getTransactionStatus: async (idOrReference) => {
    try {
      // Consultar solo API local
      const localResponse = await apiRequest(`/transactions/status/${idOrReference}`)
      return localResponse
    } catch (error) {
      console.error('Error al obtener estado de transacción:', error)
      throw error
    }
  },

  updateTransactionStatus: async (reference, status) => {
    try {
      // Intentar primero con el endpoint autenticado
      const token = getToken()
      let endpoint = `/transactions/update-status/${reference}`
      
      // Si no hay token, usar el endpoint público
      if (!token) {
        endpoint = `/transactions/update-status-public/${reference}`
      }
      
      const response = await apiRequest(endpoint, {
        method: 'PUT',
        body: JSON.stringify({ status })
      })
      return response
    } catch (error) {
      console.error('Error al actualizar estado de transacción:', error)
      // Si falla con el endpoint autenticado, intentar con el público
      if (error.message && error.message.includes('401')) {
        try {
          const response = await apiRequest(`/transactions/update-status-public/${reference}`, {
            method: 'PUT',
            body: JSON.stringify({ status })
          })
          return response
        } catch (publicError) {
          console.error('Error al actualizar estado con endpoint público:', publicError)
          throw publicError
        }
      }
      throw error
    }
  }
}

// Función para verificar la salud del API
export const healthCheck = async () => {
  try {
    return await apiRequest('/health')
  } catch (error) {
    console.error('Backend no disponible:', error)
    throw error
  }
}

// HTTP methods wrapper
const api = {
  get: async (endpoint) => {
    return await apiRequest(endpoint, { method: 'GET' })
  },
  post: async (endpoint, data) => {
    return await apiRequest(endpoint, {
      method: 'POST',
      body: JSON.stringify(data)
    })
  },
  put: async (endpoint, data) => {
    return await apiRequest(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data)
    })
  },
  delete: async (endpoint) => {
    return await apiRequest(endpoint, { method: 'DELETE' })
  }
}

// Mantener compatibilidad hacia atrás
export const sorteoService = membresiaService

export default api